import { EventAttendeeRole, EventAttendeeState, FullEvent } from 'graphql/generated/scheme'
import humanNumber from 'human-number'
import { CountryCode, isPossiblePhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js'
import moment, { Moment } from 'moment-timezone'
import FileResizer from 'react-image-file-resizer'
import { marked } from 'marked'
import { DEFAULT_EXTENDED_MINUTES, SOCIAL_CONNECTIONS } from './constants'
import { linkifyHtmlTextNodes } from './linkify'
import { EventsPageSearchEventType } from 'pages/Events/events.type'
import { EventsManageMyEventsEventType } from 'pages/RepClubs/Events/event.type'

/**
 * Process rich text HTML content and automatically detect/linkify URLs
 * while preserving existing HTML formatting
 * @param html - The HTML string to process
 * @returns HTML string with URLs converted to clickable links
 */
export const processRichTextWithLinks = (html: string): string => {
	if (!html) return ''

	// URL regex pattern
	const urlRegex = /(https?:\/\/[^\s<>"']+)/gi

	// Function to check if a URL is already within an <a> tag
	const isUrlInLink = (text: string, urlIndex: number): boolean => {
		const beforeUrl = text.substring(0, urlIndex)
		const afterUrl = text.substring(urlIndex)

		// Find the last opening <a> tag before the URL
		const lastOpenTag = beforeUrl.lastIndexOf('<a')
		const lastCloseTag = beforeUrl.lastIndexOf('</a>')

		// If there's an opening tag after the last closing tag, we might be inside a link
		if (lastOpenTag > lastCloseTag) {
			// Find the corresponding closing tag after the URL
			const nextCloseTag = afterUrl.indexOf('</a>')
			return nextCloseTag !== -1
		}

		return false
	}

	/** Avoid wrapping `src` / `href` / `poster` values (breaks `<iframe>`, `<img>`, video, etc.). */
	const isUrlInsideQuotedAttribute = (text: string, urlIndex: number): boolean => {
		const before = text.slice(0, urlIndex)
		return (
			/\s(?:src|href|poster)\s*=\s*"(?:[^"]*)$/is.test(before) ||
			/\s(?:src|href|poster)\s*=\s*'(?:[^']*)$/is.test(before)
		)
	}

	// Process the HTML to add links to URLs not already linked
	return html.replace(urlRegex, (match, url, offset) => {
		// Check if this URL is already inside an <a> tag
		if (isUrlInLink(html, offset)) {
			return match // Return unchanged if already linked
		}

		if (isUrlInsideQuotedAttribute(html, offset)) {
			return match
		}

		// Create a clickable link with icon
		return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 text-link transition-all duration-300 underline hover:text-link-hover">
			<svg class="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
				<path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H6.9C4.01 7 1.8 9.21 1.8 12s2.21 5 5.1 5h4v-1.9H6.9c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9.1-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.89 0 5.1-2.21 5.1-5s-2.21-5-5.1-5z"/>
			</svg>
			${url}
		</a>`
	})
}

/** True when serialized HTML clearly contains real element tags (not plain text only). */
const serializedHtmlHasElements = (html: string): boolean =>
	/<[a-z][a-z0-9]*\b/i.test(html) ||
	/<\/[a-z][a-z0-9]*\s*>/i.test(html) ||
	/<style\b/i.test(html) ||
	/<iframe\b/i.test(html) ||
	/<!DOCTYPE/i.test(html) ||
	/<html\b/i.test(html)

/** True when the string still contains entity-encoded tag opens (needs another innerHTML round-trip). */
const hasEncodedTagOpen = (html: string): boolean => /&lt;[a-z]/i.test(html)

/**
 * Fix common paste/CMS issues so the browser can parse the fragment.
 * Word/CMS often uses Unicode en/em/figure dashes in `<!--` / `-->` so the parser never enters a real
 * comment and the rest of the document can end up as one bogus text node → "all source as plain text".
 */
const normalizePastedEventDescriptionHtml = (s: string): string => {
	let out = s
	out = out
		.replace(/\uFF1C/g, '<')
		.replace(/\uFF1E/g, '>')
		.replace(/\uFF06/g, '&')
	// `<!` + any typographic/minus dash → `<!--` (hyphen-minus U+002D is already valid as second char)
	out = out.replace(/<![\u2010\u2011\u2012\u2013\u2014\u2212]/gi, '<!--')
	// `–>` / `—>` style fake closers → `-->`
	out = out.replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]>/g, '-->')
	// Bare `&` between spaces (e.g. "Parking & Travel") — must be `&amp;` in HTML text
	out = out.replace(/(\s)&(\s)/g, '$1&amp;$2')
	out = out.replace(/\b(src|href)=(["'])\s*\n\s*/gi, '$1=$2')
	out = out.replace(/(https?:\/\/[^"'<>\s]+)\s*\n\s*(["'])/gi, '$1$2')
	return out
}

/**
 * Decode HTML entity layers (`&lt;` → `<`, `&amp;lt;` → …) using the textarea trick.
 * For strings that are already real markup, `.value` round-trips unchanged (safe to always run).
 */
const decodeHtmlEntityLayersWithTextarea = (input: string): string => {
	if (typeof document === 'undefined') {
		return input
	}
	let cur = input
	for (let pass = 0; pass < 8; pass += 1) {
		const ta = document.createElement('textarea')
		ta.innerHTML = cur
		const next = ta.value
		if (next === cur) {
			break
		}
		cur = next
	}
	return cur
}

/**
 * `via.placeholder.com` / `placehold.it` often fail in production (service deprecated). Map to placehold.co
 * so `<img src="…">` in event descriptions still load.
 */
const rewriteLegacyPlaceholderImageHosts = (html: string): string => {
	let out = html
	out = out.replace(
		/https?:\/\/via\.placeholder\.com\/(\d+)(?:x(\d+))?(\?[^"'>\s]*)?/gi,
		(_full, w: string, h: string | undefined, query: string | undefined) => {
			const hh = h || w
			const q = query?.startsWith('?') ? query : ''
			return `https://placehold.co/${w}x${hh}${q}`
		}
	)
	out = out.replace(
		/https?:\/\/placehold\.it\/(\d+)(?:x(\d+))?(\?[^"'>\s]*)?/gi,
		(_full, w: string, h: string | undefined, query: string | undefined) => {
			const hh = h || w
			const q = query?.startsWith('?') ? query : ''
			return `https://placehold.co/${w}x${hh}${q}`
		}
	)
	return out
}

/**
 * When the parser treats markup as **text**, `innerHTML` serializes `<` as `&lt;` — our element check
 * then fails and we wrongly fall back to linkify. Re-assigning through a div peels entity layers until
 * real tags appear or the string stabilizes.
 */
const refineHtmlThroughInnerHtml = (input: string): string => {
	let cur = input
	for (let pass = 0; pass < 12; pass++) {
		const div = document.createElement('div')
		div.innerHTML = cur
		const next = div.innerHTML
		const hasElements = serializedHtmlHasElements(next)
		const encodedLeft = hasEncodedTagOpen(next)
		if (hasElements && !encodedLeft) {
			return next
		}
		if (next === cur) {
			if (hasElements) {
				return next
			}
			break
		}
		cur = next
	}
	return cur
}

function prepareEventDescriptionNoDocument(s: string): string {
	if (/<\s*iframe\b/i.test(s)) {
		return s
	}
	return serializedHtmlHasElements(s) && !hasEncodedTagOpen(s) ? s : processRichTextWithLinks(s)
}

function parseHtmlBodyInnerHtml(s: string): string {
	try {
		const doc = new DOMParser().parseFromString(s, 'text/html')
		return doc.body.innerHTML
	} catch {
		return s
	}
}

function prepareEventDescriptionWithDocument(s: string): string {
	let parsed = parseHtmlBodyInnerHtml(s)
	parsed = refineHtmlThroughInnerHtml(parsed)

	if (!serializedHtmlHasElements(parsed) || hasEncodedTagOpen(parsed)) {
		const direct = refineHtmlThroughInnerHtml(s)
		if (serializedHtmlHasElements(direct)) {
			parsed = direct
		}
	}

	if (serializedHtmlHasElements(parsed)) {
		return linkifyHtmlTextNodes(parsed)
	}

	// Embeds (YouTube iframe, etc.): never linkify — would corrupt `src="https://..."`.
	if (/<\s*iframe\b/i.test(s)) {
		return s
	}

	return processRichTextWithLinks(s)
}

function prepareEventDescriptionFallbackDom(raw: string): string {
	let fallback = normalizePastedEventDescriptionHtml(raw.trim())
	fallback = decodeHtmlEntityLayersWithTextarea(fallback)
	fallback = rewriteLegacyPlaceholderImageHosts(fallback)
	const div = document.createElement('div')
	div.innerHTML = fallback
	return div.innerHTML
}

/**
 * True when the raw HTML contains at least one non-whitespace character of text
 * (parses the DOM, ignoring markup-only content like `<p><br></p>` or `&nbsp;`).
 * For plain-text fields like FAQ questions; rich-text sections gate on {@link hasVisibleContent}.
 */
export const hasVisibleText = (html?: string | null): boolean => !!parseRenderedBody(html)?.textContent?.trim()

/**
 * Like {@link hasVisibleText}, but embedded media counts too: a check-in note or About section that is
 * only an image (e.g. an uploaded venue map or flyer) is still something to show, so section wrappers
 * don't hide it — while markup-only input still renders no empty shell. Only media DOMPurify keeps by default.
 */
export const hasVisibleContent = (html?: string | null): boolean => {
	const body = parseRenderedBody(html)
	return !!body && (!!body.textContent?.trim() || !!body.querySelector('img, video, audio, iframe'))
}

const parseRenderedBody = (html?: string | null): HTMLElement | null => {
	if (!html) return null
	const doc = new DOMParser().parseFromString(html, 'text/html')
	doc.querySelectorAll('script, style, template').forEach(el => el.remove())
	return doc.body
}

/**
 * Event About / description: HTML + optional entity-encoded chunks + `<style>`.
 * Never run {@link processRichTextWithLinks} on structured HTML (breaks `src="https://..."`); bare URLs in
 * structured HTML are linkified through {@link linkifyHtmlTextNodes}, which only touches text nodes.
 */
export const prepareEventDescriptionHtml = (raw: string): string => {
	try {
		if (!raw) {
			return ''
		}

		let s = raw.trim()
		if (
			(s.startsWith('"') && s.endsWith('"') && s.length > 1) ||
			(s.startsWith("'") && s.endsWith("'") && s.length > 1)
		) {
			s = s.slice(1, -1).trim()
		}

		s = normalizePastedEventDescriptionHtml(s)
		s = decodeHtmlEntityLayersWithTextarea(s)
		s = rewriteLegacyPlaceholderImageHosts(s)

		if (typeof document === 'undefined') {
			return prepareEventDescriptionNoDocument(s)
		}

		return prepareEventDescriptionWithDocument(s)
	} catch (error) {
		console.error('prepareEventDescriptionHtml failed:', error)
		if (typeof document === 'undefined') {
			return raw.trim()
		}
		try {
			return prepareEventDescriptionFallbackDom(raw)
		} catch {
			return raw.trim()
		}
	}
}

/**
 * Converts Markdown content back to HTML format
 * For displaying stored markdown content in the UI
 * @param markdown - The Markdown string to convert to HTML
 * @returns HTML formatted string
 */
export const convertMarkdownToHtml = (markdown: string): string => {
	if (!markdown) return ''

	try {
		// Step 1: Handle multiple consecutive newlines BEFORE marked processing
		// Convert groups of 3+ newlines to preserve visual spacing
		const processedMarkdown = markdown.replace(/\n{4,}/g, match => {
			// For every group of 4+ newlines, keep 2 for paragraph and add <br> for extras
			const extraBreaks = match.length - 2
			return '\n\n' + '<br />'.repeat(extraBreaks) + '\n\n'
		})

		// Step 2: Convert with marked (GFM disabled to prevent auto-linking)
		let html = marked(processedMarkdown, {
			gfm: false, // Disable auto-linking
		})

		// Step 3: Ensure string and apply link processing
		html = String(html)

		// Step 4: Apply manual link processing for plain URLs
		return processRichTextWithLinks(html)
	} catch (error) {
		console.error('Error converting Markdown to HTML:', error)
		return markdown
	}
}

/**
 * Detects if a string is likely a phone number
 * @param value - The input string to check
 * @returns boolean indicating if the value appears to be a phone number
 */
export const isPhoneNumber = (value: string): boolean => {
	if (!value || value.trim().length === 0) {
		return false
	}

	// Clean the input by removing spaces, dashes, and parentheses
	const cleanValue = value.replace(/[\s\-()]/g, '')

	// Check if it's mostly digits with optional + at the start
	const phoneRegex = /^\+?[\d]{7,15}$/
	if (!phoneRegex.test(cleanValue)) {
		return false
	}

	// Use libphonenumber-js for more robust validation
	try {
		return isPossiblePhoneNumber(value)
	} catch (_error) {
		// Fallback to basic regex check if parsing fails
		return phoneRegex.test(cleanValue)
	}
}

export const numberFormat = (number: number, force2Decimal?: boolean, isHumanNumber?: boolean) => {
	if (number === 0) {
		return `${number}`
	}
	const options: any = {}
	if (force2Decimal) {
		if (isHumanNumber) {
			return humanNumber(number, n => {
				let number = n
				if (number > 100) {
					number = Math.floor(number)
				}
				return Number.parseFloat(number.toPrecision(3))
					.toFixed(2)
					.replace(/[.,]?0+$/, '')
			})
		}
		options.minimumFractionDigits = 2
	}
	options.maximumFractionDigits = 2
	if (isHumanNumber) {
		return humanNumber(number)
	}
	return new Intl.NumberFormat(undefined, options).format(number)
}

export interface CurrencyInfo {
	code: string
	symbol: string
	isZeroDecimal: boolean
	name: string
}

export interface CurrencyNumericProps {
	thousandSeparator: string
	decimalSeparator: string
	decimalScale: number
}

export interface ResolvedCurrency {
	/** Normalized uppercase currency code, e.g. "USD" */
	code: string
	/** Currency symbol, e.g. "$", "£", "¥" */
	symbol: string
	/** Intl locale string, e.g. "en-US", "pt-BR", "ja-JP" */
	locale: string
	/** True for zero-decimal currencies like JPY */
	isZeroDecimal: boolean
	/** Props for react-number-format's NumericFormat component */
	numericProps: CurrencyNumericProps
	/** Format a monetary amount using locale-aware Intl.NumberFormat */
	format(amount: number): string
}

const CURRENCY_LOCALE_MAP: Record<string, string> = {
	GBP: 'en-GB',
	EUR: 'en-IE',
	BRL: 'pt-BR',
	JPY: 'ja-JP',
}

/**
 * Single source of truth for all currency-derived properties.
 * Pass the currency code and optional currencies array (from API) to get
 * symbol, locale, isZeroDecimal, NumericFormat props, and a format() method.
 */
export const resolveCurrency = (code?: string | null, currencies?: CurrencyInfo[]): ResolvedCurrency => {
	const normalizedCode = (code ?? 'USD').toUpperCase().trim()
	const found = currencies?.find(c => c.code.toUpperCase() === normalizedCode)

	// Prefer API-provided symbol, then derive from Intl.NumberFormat, finally '$'
	let symbol = found?.symbol
	if (!symbol) {
		try {
			const symParts = new Intl.NumberFormat('en', {
				style: 'currency',
				currency: normalizedCode,
				currencyDisplay: 'narrowSymbol',
			}).formatToParts(0)
			symbol = symParts.find(p => p.type === 'currency')?.value
		} catch {
			// unknown currency code — fall back below
		}
		symbol = symbol ?? '$'
	}

	const locale = CURRENCY_LOCALE_MAP[normalizedCode] ?? 'en-US'
	// Prefer the API-supplied flag; fall back to Intl's own knowledge so that
	// zero-decimal currencies like JPY are handled correctly even when the
	// currencies array is absent or incomplete.
	let isZeroDecimal: boolean
	if (found?.isZeroDecimal !== undefined) {
		isZeroDecimal = found.isZeroDecimal
	} else {
		try {
			isZeroDecimal =
				new Intl.NumberFormat(locale, { style: 'currency', currency: normalizedCode })
					.resolvedOptions().maximumFractionDigits === 0
		} catch {
			isZeroDecimal = false
		}
	}

	const parts = new Intl.NumberFormat(locale).formatToParts(1234567.89)
	const numericProps: CurrencyNumericProps = {
		thousandSeparator: parts.find(p => p.type === 'group')?.value ?? ',',
		decimalSeparator: parts.find(p => p.type === 'decimal')?.value ?? '.',
		decimalScale: isZeroDecimal ? 0 : 2,
	}

	const format = (amount: number): string => {
		if (isNaN(amount)) return '---'
		try {
			return new Intl.NumberFormat(locale, {
				style: 'currency',
				currency: normalizedCode,
				minimumFractionDigits: isZeroDecimal ? 0 : 2,
				maximumFractionDigits: isZeroDecimal ? 0 : 2,
			}).format(amount)
		} catch {
			return currencyFormat(amount, symbol)
		}
	}

	return { code: normalizedCode, symbol, locale, isZeroDecimal, numericProps, format }
}

// Convenience wrappers — delegate to resolveCurrency for backward compatibility

export const getCurrencySymbol = (currency?: string | null, currencies?: CurrencyInfo[]): string =>
	resolveCurrency(currency, currencies).symbol

export const getCurrencyLocale = (currency?: string | null): string => resolveCurrency(currency).locale

export const getCurrencyNumericProps = (code?: string | null, currencies?: CurrencyInfo[]): CurrencyNumericProps =>
	resolveCurrency(code, currencies).numericProps

export const formatCurrencyAmount = (amount: number, code?: string | null, currencies?: CurrencyInfo[]): string =>
	resolveCurrency(code, currencies).format(amount)

export const currencyFormat = (
	number: number,
	unit: string = '$',
	force2Decimal: boolean = true,
	isHumanNumber?: boolean
) => {
	if (isNaN(number)) {
		return '---'
	}
	return `${unit}${numberFormat(number, force2Decimal, isHumanNumber)}`
}

export const volatilityFormat = (number: number, force2Decimal: boolean = true) => {
	if (number > 0) {
		return `+${numberFormat(number, force2Decimal)}`
	}
	return numberFormat(number, force2Decimal)
}

export const slugify = (str: string) =>
	str
		.toLowerCase()
		.trim()
		.replace(/[^\w\s-]/g, '')
		.replace(/[\s_-]+/g, '-')
		.replace(/^-+|-+$/g, '')

export const isShowRank = (rank: any) => {
	return rank && rank > 0 && rank <= 1000
}

export const toCamelCase = (_key: any, value: any) => {
	if (value && typeof value === 'object') {
		for (const k in value) {
			if (/^[A-Z]/.test(k) && Object.hasOwnProperty.call(value, k)) {
				value[k.charAt(0).toLowerCase() + k.substring(1)] = value[k]
				delete value[k]
			}
		}
	}
	return value
}

export const todayUTC = () => {
	const localDate = new Date()
	return new Date(localDate.toISOString())
}

export const camelCaseToWords = (text: string) => {
	return text.replace(/([a-z])([A-Z])/g, '$1 $2')
}

export const toProperCase = (text: string) => {
	return text.replace(/\w\S*/g, txt => {
		return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
	})
}

export const toOrdinalNumber = (i: number) => {
	const j = i % 10,
		k = i % 100
	if (j == 1 && k != 11) {
		return i + 'st'
	}
	if (j == 2 && k != 12) {
		return i + 'nd'
	}
	if (j == 3 && k != 13) {
		return i + 'rd'
	}
	return i + 'th'
}

export const getSocialConnectionConstant = (scheme: string) => {
	return SOCIAL_CONNECTIONS.find(x => x.scheme === scheme)
}

export const toDatetime = (startDate: string, timeZone?: string) => {
	const date = moment(startDate)
	if (timeZone) {
		return date.tz(timeZone)
	}
	return date
}

export const formatDatetime = (startDate: string, timeZone?: string, format?: string) => {
	return toDatetime(startDate, timeZone).format(format || 'MMM D, yyyy h:mm A')
}

export const formatDatetimeRange = (startDate: string, endDate: string, timeZone?: string) => {
	const sDate = toDatetime(startDate, timeZone)
	const eDate = toDatetime(endDate, timeZone)

	const startFormat = formatDatetime(startDate, timeZone)
	// No range to show when the end isn't after the start
	if (!eDate.isAfter(sDate)) return startFormat

	// Drop the date from the end only when it is the same calendar day as the start
	const endFormat = formatDatetime(endDate, timeZone, eDate.isSame(sDate, 'day') ? 'h:mm A' : 'MMM D, yyyy h:mm A')
	return `${startFormat} - ${endFormat}`
}

/**
 * The event's date and time split for display, all read in the event's timezone:
 * `month`/`day` for a calendar tile, `dateLine` ("Sunday, October 18", or a two-day span) and `timeLine`
 * ("1:00 PM - 5:00 PM", or just the start when the end isn't after it). The year only appears when it isn't this year.
 */
export const formatEventDateParts = (startDate: string, endDate?: string | null, timeZone?: string) => {
	const start = toDatetime(startDate, timeZone)
	const end = endDate ? toDatetime(endDate, timeZone) : null
	const hasRange = !!end && end.isAfter(start)
	const now = timeZone ? moment().tz(timeZone) : moment()
	const yearSuffix = (date: Moment) => (date.isSame(now, 'year') ? '' : ', YYYY')

	let dateLine = start.format(`dddd, MMMM D${yearSuffix(start)}`)
	if (hasRange && !end.isSame(start, 'day')) {
		const startDay = start.format(`ddd, MMM D${yearSuffix(start)}`)
		const endDay = end.format(`ddd, MMM D${yearSuffix(end)}`)
		dateLine = `${startDay} - ${endDay}`
	}
	const timeLine = hasRange
		? `${start.format('h:mm A')} - ${end.format('h:mm A')}`
		: start.format('h:mm A')

	return { month: start.format('MMM').toUpperCase(), day: start.format('D'), dateLine, timeLine }
}

export const convertToUTC = (localTime: string, timezone: string) => {
	const localMoment = moment.tz(localTime, timezone ?? 'utc')
	return localMoment.utc()
}

export const convertToLocalTime = (utcTime: string, timezone: string) => {
	const utcMoment = moment.utc(utcTime)
	return utcMoment.tz(timezone ?? 'utc')
}

export const resizeFile = (file: File) =>
	new Promise(resolve => {
		FileResizer.imageFileResizer(
			file,
			1000,
			1000,
			'WEBP',
			100,
			0,
			(uri: any) => {
				resolve(uri)
			},
			'file'
		)
	})

export const getCityFromTimezone = (timezoneCode: string): string => {
	const parts = timezoneCode.split('/')
	return parts.length > 1 ? parts[parts.length - 1].replace(/_/g, ' ') : 'Unknown City'
}

export const formatTimezoneWithDST = (tzCode: string) => {
	const city = getCityFromTimezone(tzCode)
	const offset = moment.tz(tzCode).format('Z')
	return `${city} (GMT${offset})`
}

export const googleMapsUrl = (latitude?: any, longitude?: any, address?: string, placeId?: string) => {
	if (!latitude || !longitude) {
		return ''
	}

	// If we have a place ID, use it for better accuracy
	if (placeId) {
		return `https://www.google.com/maps/place/?q=place_id:${placeId}`
	}

	// If we have an address, include it in the query for better context
	if (address) {
		const encodedAddress = encodeURIComponent(address)
		return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}&center=${latitude},${longitude}`
	}

	// Default: just use coordinates
	return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`
}

// Additional utility functions for Google Maps
export const getDirectionsUrl = (
	fromLat?: number,
	fromLng?: number,
	toLat?: number,
	toLng?: number,
	address?: string
) => {
	if (!toLat || !toLng) {
		return ''
	}

	let origin = ''
	if (fromLat && fromLng) {
		origin = `&origin=${fromLat},${fromLng}`
	}

	const destination = address ? encodeURIComponent(address) : `${toLat},${toLng}`
	return `https://www.google.com/maps/dir/?api=1${origin}&destination=${destination}`
}

export const getStaticMapUrl = (
	latitude: number,
	longitude: number,
	zoom: number = 15,
	width: number = 400,
	height: number = 300
) => {
	const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
	if (!apiKey) {
		return ''
	}

	return `https://maps.googleapis.com/maps/api/staticmap?center=${latitude},${longitude}&zoom=${zoom}&size=${width}x${height}&markers=color:red%7C${latitude},${longitude}&key=${apiKey}`
}

export const lightenColor = (color: string, amount: number = 0.3): string => {
	// Check if color is in hex format
	if (!color.startsWith('#')) {
		return color
	}

	let hex = color.substring(1)

	// Handle shorthand hex (e.g. #FFF)
	if (hex.length === 3) {
		hex = hex
			.split('')
			.map(c => c + c)
			.join('')
	}

	// Convert hex to RGB
	let r = parseInt(hex.substring(0, 2), 16)
	let g = parseInt(hex.substring(2, 4), 16)
	let b = parseInt(hex.substring(4, 6), 16)

	// Lighten the color
	r = Math.min(255, Math.round(r + (255 - r) * amount))
	g = Math.min(255, Math.round(g + (255 - g) * amount))
	b = Math.min(255, Math.round(b + (255 - b) * amount))

	// Convert back to hex
	return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

export const darkenColor = (color: string, amount: number = 0.3): string => {
	// Check if color is in hex format
	if (!color.startsWith('#')) {
		return color
	}

	let hex = color.substring(1)

	// Handle shorthand hex (e.g. #FFF)
	if (hex.length === 3) {
		hex = hex
			.split('')
			.map(c => c + c)
			.join('')
	}

	// Convert hex to RGB
	let r = parseInt(hex.substring(0, 2), 16)
	let g = parseInt(hex.substring(2, 4), 16)
	let b = parseInt(hex.substring(4, 6), 16)

	// Darken the color
	r = Math.max(0, Math.round(r * (1 - amount)))
	g = Math.max(0, Math.round(g * (1 - amount)))
	b = Math.max(0, Math.round(b * (1 - amount)))

	// Convert back to hex
	return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

/**
 * Calculates the contrasting text color (black or white) for a given background color.
 *
 * @param backgroundColor - The background color in hex format (e.g., "#RRGGBB" or "#RGB").
 * @returns The contrasting text color ("#000000" for black or "#FFFFFF" for white).
 */
export function getContrastingTextColor(backgroundColor: string): string {
	if (!backgroundColor) {
		return '#FFFFFF' // Default to black if no color is provided
	}

	// Remove the '#' if it exists
	let hex = backgroundColor.startsWith('#') ? backgroundColor.slice(1) : backgroundColor

	// Handle shorthand hex codes (#RGB)
	if (hex.length === 3) {
		hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
	}

	if (hex.length !== 6) {
		console.error('Invalid hex color format provided.')
		return '#000000' // Default to black on error
	}

	// Convert hex to RGB
	const r = parseInt(hex.substring(0, 2), 16)
	const g = parseInt(hex.substring(2, 4), 16)
	const b = parseInt(hex.substring(4, 6), 16)

	// Calculate relative luminance using the formula for sRGB
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

	// Use a threshold (e.g., 0.5) to decide whether to return black or white
	return luminance > 0.5 ? '#000000' : '#FFFFFF'
}

export function getISOWeekOfMonth(date: Moment) {
	const firstDayOfMonth = moment(date).startOf('month')
	return date.isoWeek() - firstDayOfMonth.isoWeek() + 1
}

export const singularize = (title: string): string => {
	// Handle common plural endings
	if (title.endsWith('s')) {
		if (title.endsWith('ies')) {
			return title.slice(0, -3) + 'y' // e.g., "Categories" -> "Category"
		}
		return title.slice(0, -1) // e.g., "Promoters" -> "Promoter"
	}
	return title // Already singular
}

export const getTheRightExtendedDate = (timeZoneId: string, momentDate?: Moment) => {
	const extendedDate = convertToLocalTime((momentDate || moment()).toISOString(), timeZoneId).add(
		DEFAULT_EXTENDED_MINUTES,
		'minutes'
	)
	const minutes = extendedDate.minutes()
	const roundedMinutes = Math.round(minutes / 15) * 15
	extendedDate.minutes(roundedMinutes).seconds(0).milliseconds(0)

	return extendedDate
}

export const getTimezoneDisplayName = (timezone: string, offsetInMinutes: number) => {
	const offset = offsetInMinutes / 60

	const timezoneMap: Record<string, string> = {
		// UTC/GMT
		'0': 'GMT', // Greenwich Mean Time (+00)
		// Positive offsets (East of UTC)
		'1': 'CET', // Central European Time (+01)
		'2': 'EET', // Eastern European Time (+02)
		'3': 'MSK', // Moscow Standard Time (+03)
		'3.5': 'IRST', // Iran Standard Time (+03:30)
		'4': 'GST', // Gulf Standard Time (+04)
		'4.5': 'AFT', // Afghanistan Time (+04:30)
		'5': 'PKT', // Pakistan Standard Time (+05)
		'5.5': 'IST', // Indian Standard Time (+05:30)
		'5.75': 'NPT', // Nepal Time (+05:45)
		'6': 'BST', // Bangladesh Standard Time (+06)
		'6.5': 'MMT', // Myanmar Time (+06:30)
		'7': 'ICT', // Indochina Time (+07)
		'8': 'SGT', // Singapore Time (+08)
		'8.75': 'ACWST', // Australian Central Western Standard Time (+08:45)
		'9': 'JST', // Japan Standard Time (+09)
		'9.5': 'ACST', // Australian Central Standard Time (+09:30)
		'10': 'AEST', // Australian Eastern Standard Time (+10)
		'10.5': 'LHST', // Lord Howe Standard Time (+10:30)
		'11': 'AEDT', // Australian Eastern Daylight Time (+11)
		'12': 'NZST', // New Zealand Standard Time (+12)
		'12.75': 'CHAST', // Chatham Standard Time (+12:45)
		'13': 'NZDT', // New Zealand Daylight Time (+13)
		'14': 'LINT', // Line Islands Time (+14)
		// Negative offsets (West of UTC)
		'-1': 'AZOT', // Azores Time (-01)
		'-2': 'GST', // South Georgia Time (-02)
		'-3': 'ART', // Argentina Time (-03)
		'-3.5': 'NST', // Newfoundland Standard Time (-03:30)
		'-4': 'AST', // Atlantic Standard Time (-04)
		'-5': 'EST', // Eastern Standard Time (-05)
		'-6': 'CST', // Central Standard Time (-06)
		'-7': 'MST', // Mountain Standard Time (-07)
		'-8': 'PST', // Pacific Standard Time (-08)
		'-9': 'AKST', // Alaska Standard Time (-09)
		'-9.5': 'MART', // Marquesas Time (-09:30)
		'-10': 'HST', // Hawaii Standard Time (-10)
		'-11': 'SST', // Samoa Standard Time (-11)
		'-12': 'BIT', // Baker Island Time (-12)
	}

	if (timezone && timezone !== 'Z' && !timezone.startsWith('+') && !timezone.startsWith('-')) {
		return timezone
	}

	const key = offset.toString()

	return timezoneMap[key] || timezone
}

export const formatTimezone = (timeZone: string) => {
	const momentTz = moment.tz(timeZone)
	const offsetInMinutes = momentTz.utcOffset()
	const tzAbbr = momentTz.format('z')

	return getTimezoneDisplayName(tzAbbr, offsetInMinutes)
}

export const arePhoneNumbersEqual = (phone1Str: string, phone2Str: string, defaultRegion: string = 'ZZ'): boolean => {
	try {
		const number1 = phone1Str
			? parsePhoneNumberFromString(phone1Str, { defaultCountry: defaultRegion as CountryCode })
			: ''
		const number2 = phone2Str
			? parsePhoneNumberFromString(phone2Str, { defaultCountry: defaultRegion as CountryCode })
			: ''

		if (!number1 || !number2 || !isPossiblePhoneNumber(phone1Str) || !isPossiblePhoneNumber(phone2Str)) {
			return false
		}

		return number1.number === number2.number
	} catch (error) {
		console.error('Error parsing phone number:', error)
		return false
	}
}

export const formatPhoneNumber = (
	phoneStr: string,
	defaultRegion: string = 'US',
	formatType: 'E164' | 'INTERNATIONAL' | 'NATIONAL' | 'RFC3966' = 'INTERNATIONAL'
): string => {
	if (!phoneStr) return ''

	try {
		const parsedNumber = phoneStr
			? parsePhoneNumberFromString(phoneStr, { defaultCountry: defaultRegion as CountryCode })
			: ''

		if (!parsedNumber || !isPossiblePhoneNumber(phoneStr)) {
			return phoneStr
		}

		switch (formatType) {
			case 'E164':
				return parsedNumber.format('E.164')
			case 'INTERNATIONAL':
				return parsedNumber.format('INTERNATIONAL')
			case 'NATIONAL':
				return parsedNumber.format('NATIONAL')
			case 'RFC3966':
				return parsedNumber.format('RFC3966')
			default:
				return parsedNumber.format('INTERNATIONAL')
		}
	} catch (error) {
		console.error('Error parsing phone number:', error)
		return phoneStr
	}
}

/**
 * Sanitizes and formats a phone number
 * Attempts to parse with libphonenumber-js and falls back to simple cleaning
 * @param rawPhone - The raw phone number string
 * @param defaultRegion - Default country code for parsing when no country code is present (defaults to undefined for international parsing)
 * @returns Formatted phone number in E.164 format or cleaned string
 */
export const sanitizePhoneNumber = (rawPhone: string, defaultRegion?: string): string => {
	if (!rawPhone) return ''

	const cleanedPhone = rawPhone.toString().trim()

	if (!cleanedPhone) return ''

	// Fallback function for simple cleaning
	const simpleFallback = (phone: string) =>
		phone.startsWith('+') ? '+' + phone.slice(1).replace(/\D+/g, '') : phone.replace(/\D+/g, '')

	try {
		// First attempt: Parse without default country (works for numbers with country codes)
		let parsedNumber = parsePhoneNumberFromString(cleanedPhone)

		// Second attempt: Use default region if first attempt failed and region is provided
		if (!parsedNumber && defaultRegion) {
			parsedNumber = parsePhoneNumberFromString(cleanedPhone, { defaultCountry: defaultRegion as CountryCode })
		}

		if (parsedNumber) {
			// Return E.164 format for consistent storage
			return parsedNumber.format('E.164')
		} else {
			// Fallback to simple cleaning if parsing fails
			return simpleFallback(cleanedPhone)
		}
	} catch (_error) {
		// Fallback to simple cleaning if parsing throws
		return simpleFallback(cleanedPhone)
	}
}

/**
 * The RSVP state to show for an event: the viewer's own attendee state, else their season-pass state (read from
 * the series' parent event). A pass-only holder has no attendee record on the member event — or only the empty
 * NONE stub a series-role holder gets — so the pass state fills in.
 */
export const resolveRsvpState = (
	ownState?: EventAttendeeState | string | null,
	seriesPassState?: EventAttendeeState | string | null
): EventAttendeeState | undefined => {
	const state = ownState && ownState !== EventAttendeeState.None ? ownState : (seriesPassState ?? ownState)
	return (state ?? undefined) as EventAttendeeState | undefined
}

export const getHostStatus = (
	event: Partial<FullEvent> | Partial<EventsPageSearchEventType> | EventsManageMyEventsEventType,
	user: any
): 'Producer' | 'Manager' | 'Promoter' | 'Sponsor' | 'Host' | 'Scanner' | null => {
	if (event?.createdBy?.userId === user?.userId) {
		return 'Producer'
	}

	if (event?.coHosts?.nodes?.some((x: any) => x.userId === user?.userId)) {
		return 'Manager'
	}

	if (event?.promoters?.nodes?.some((x: any) => x.userId === user?.userId)) {
		return 'Promoter'
	}

	if (event?.sponsors?.nodes?.some((x: any) => x.userId === user?.userId)) {
		return 'Sponsor'
	}

	if (event?.attendeeInfo?.attendeeInfo?.attendeeRole === EventAttendeeRole.Coordinator) {
		return 'Host'
	}

	if (event?.attendeeInfo?.attendeeInfo?.attendeeRole === EventAttendeeRole.Door) {
		return 'Scanner'
	}

	return null
}

type RemoveTypename<T> =
	T extends Array<infer U>
		? Array<RemoveTypename<U>>
		: T extends object
			? {
					[K in keyof T as K extends '__typename' ? never : K]: RemoveTypename<T[K]>
				}
			: T

export function removeTypename<T>(obj: T): RemoveTypename<T> {
	if (obj === null || obj === undefined || typeof obj !== 'object') {
		return obj as RemoveTypename<T>
	}

	if (Array.isArray(obj)) {
		return obj.map(item => removeTypename(item)) as RemoveTypename<T>
	}

	const result: any = {}
	for (const key in obj) {
		if (obj[key] && key !== '__typename') {
			result[key] = removeTypename(obj[key])
		}
	}

	return result
}
